import React, { useContext, useRef, useLayoutEffect, useState } from 'react';
import PropTypes from 'prop-types';
import gsap from 'gsap';

import { CustomEaseJorisInOut } from '@shcc/modules/CustomEaseModule';
import { useSidebarNavigation, useSocialMediaLinks } from '@hooks';
import { RegionsContext } from '@common/components/Region/Region';
import { REGION_TYPE } from '@common/components/layouts/MainLayout/MainLayout';
import { UserStoreContext } from '@common/stores';
import { ButtonClose } from './ButtonClose';
import { List } from './List';
import { SmallList } from './SmallList';
import { SocialList } from './SocialList';
import i18n from '@components/i18n/i18n';
import { FocusTrap, getTaxonomyLabels } from '@utils';
import ButtonToggleAnimations from './ButtonToggleAnimations';
import styles from './SidebarNavigation.module.scss';

const SidebarNavigation = ({ onClick }) => {
  const { motionDisabled, setMotionDisabled } = useContext(UserStoreContext);
  const { regions, close } = useContext(RegionsContext);
  const [hasFocus, setHasFocus] = useState(false);
  const labels = getTaxonomyLabels('header');
  const region = regions.find(({ id }) => id === REGION_TYPE.overlay) || {};

  const multiplier = i18n.dir() === 'rtl' ? -1 : 1;

  const items = useSidebarNavigation();
  const social = useSocialMediaLinks();

  const background = useRef(null);
  const buttonClose = useRef(null);
  const content = useRef(null);
  const tweens = useRef({
    transitionIn: null,
    transitionOut: null,
  });

  const transitionInit = () => {
    gsap.set(background.current, { autoAlpha: 0 });
    gsap.set(content.current, { x: content.current.clientWidth * multiplier });
  };

  const transitionIn = () => {
    tweens.current.transitionIn = gsap.timeline({
      onComplete: () => setHasFocus(true),
    });
    tweens.current.transitionIn.to(
      background.current,
      { autoAlpha: 1.0, duration: 0.4, ease: 'none' },
      0.0
    );
    tweens.current.transitionIn.to(
      content.current,
      { x: 0, duration: 0.6, ease: CustomEaseJorisInOut },
      0.0
    );
  };

  const transitionOut = callback => {
    tweens.current.transitionOut = gsap.timeline({ onComplete: callback });
    tweens.current.transitionOut.to(
      content.current,
      {
        x: content.current.clientWidth * multiplier,
        duration: 0.5,
        ease: 'power2.in',
      },
      0.0
    );
    tweens.current.transitionOut.to(
      background.current,
      { alpha: 0.0, duration: 0.4, ease: 'none' },
      0.0
    );
  };

  const clear = () => {
    close({ id: REGION_TYPE.overlay, clear: true });
  };
  const handleClickBackground = () => {
    transitionOut(() => clear());
  };

  const handleClickButtonClose = () => {
    transitionOut(() => clear());
  };

  const handleAnimationToggleClicked = () => {
    setMotionDisabled(!motionDisabled);
  };

  useLayoutEffect(() => {
    transitionInit();
    transitionIn();

    return () => {
      if (region) {
        if (tweens.current.transitionIn) {
          tweens.current.transitionIn.kill();
          tweens.current.transitionIn = null;
        }
        if (tweens.current.transitionOut) {
          tweens.current.transitionOut.kill();
          tweens.current.transitionOut = null;
        }
      }
    };
  }, []);

  const animationDisableButtonData = {
    label: motionDisabled
      ? labels['enable-animations']
      : labels['disable-animations'],
  };

  return (
    <FocusTrap className={styles.container} hasFocus={hasFocus} delayFocus>
      <button
        type="button"
        className={styles.background}
        onClick={handleClickBackground}
        ref={background}
        tabIndex="-1"
      >
        <span className="sr-only">{labels['close-side-menu']}</span>
      </button>
      <nav className={styles.content} ref={content} tabIndex="-1">
        <div className={styles.buttonClose} ref={buttonClose}>
          <ButtonClose
            className={styles.buttonClose}
            onClick={handleClickButtonClose}
            ariaLabel={labels['close-side-menu']}
            motionDisabled={motionDisabled}
          />
        </div>

        {items.map(item => {
          if (!item.children) return null;
          return item.type === 'small' ? (
            <SmallList
              items={item.children}
              onClick={onClick}
              key={item.drupal_id}
            />
          ) : (
            <List
              items={item.children}
              onClick={onClick}
              key={item.drupal_id}
            />
          );
        })}

        <SocialList
          items={social?.relationships?.field_social_link || []}
          className={styles.social}
        />

        <ButtonToggleAnimations
          ariaLabel={animationDisableButtonData.label}
          onClick={handleAnimationToggleClicked}
          motionDisabled={motionDisabled}
        >
          {animationDisableButtonData.label}
        </ButtonToggleAnimations>
      </nav>
    </FocusTrap>
  );
};

SidebarNavigation.propTypes = {
  onClick: PropTypes.func,
};

SidebarNavigation.defaultProps = {
  onClick: () => {},
};

export default SidebarNavigation;
